import { subscribeSidebar } from '../../../../design/sidebar.js';
import { Suspense, useEffect, useRef, type CSSProperties } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Menu, ScanSearch, Search } from 'lucide-react';
import { Skeleton } from '../components/blocks';
import { ToastZone } from '../components/feedback';
import { useStickyTables } from '../components/sticky';
import CommandPalette, { type CommandPaletteHandle } from './CommandPalette';
import { PORTAL_ORIGIN } from './auth';
import Sidebar from './Sidebar';

/** 外壳：左侧侧栏 + 右侧内容（DESIGN.md §2.1）；< 640 宽时侧栏收进左侧抽屉，顶上一条细栏 */
export default function Shell() {
  const cmdk = useRef<CommandPaletteHandle>(null);
  // 长表的表头吸顶（停住、跟着最后一行走）照 console.js 判断，全站挂一次
  useStickyTables();
  const { pathname } = useLocation();

  useEffect(() => {
    return subscribeSidebar(() => undefined);
  }, []);

  // 换页后收起手机抽屉
  useEffect(() => {
    delete document.documentElement.dataset.sheet;
  }, [pathname]);

  // ⌘K / Ctrl+K 开关；不在输入框里时按 / 打开（单键只在没有聚焦输入框时生效，WCAG 2.1.4）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        cmdk.current?.toggle();
        return;
      }
      const t = e.target as HTMLElement | null;
      if (e.key === '/' && !t?.closest('input,textarea,select,[contenteditable]') && !document.querySelector('dialog[open]')) {
        e.preventDefault();
        cmdk.current?.open();
      }
      if (e.key === 'Escape' && 'sheet' in document.documentElement.dataset) delete document.documentElement.dataset.sheet;
    };
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if ('sheet' in document.documentElement.dataset && !t.closest('.sb') && !t.closest('[data-sheet-toggle]')) {
        delete document.documentElement.dataset.sheet;
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick);
    };
  }, []);

  const toggleSheet = () => {
    const root = document.documentElement;
    if ('sheet' in root.dataset) delete root.dataset.sheet;
    else root.dataset.sheet = '';
  };

  return (
    <>
      <a className="skip" href="#main">跳到正文</a>
      <div className="mbar">
        <button type="button" className="btn btn-quiet btn-icon" aria-label="打开导航" data-sheet-toggle onClick={toggleSheet}>
          <Menu className="ico" aria-hidden="true" />
        </button>
        <span className="app-ic is-s" style={{ '--app': 'var(--app-diagnostics)' } as CSSProperties} aria-hidden="true">
          <ScanSearch />
        </span>
        <span className="brand-name">问题诊断</span>
        <button type="button" className="btn btn-quiet btn-icon mbar-search" aria-label="搜索" onClick={() => cmdk.current?.open()}>
          <Search className="ico" aria-hidden="true" />
        </button>
      </div>
      <div className="app">
        <Sidebar portalOrigin={PORTAL_ORIGIN} onSearch={() => cmdk.current?.open()} />
        <main className="main" id="main" tabIndex={-1}>
          {/* 页面按路由拆包：下载这一页的代码时显示骨架 */}
          <Suspense fallback={<div className="page"><Skeleton lines={6} height={320} /></div>}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <CommandPalette ref={cmdk} />
      <ToastZone />
    </>
  );
}
